import { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";

import { TicketsTab, TranscriptsTab, AnalyticsTab, BookingTab } from "./detailTabs";

import { formatDuration, getCallDurationInSeconds } from "../../api/callData";
import { formatTimeStamp } from "../../api/callData";

import { useAuthUser } from "../../contexts/AuthUserContext";
import { useCustomer } from "../../contexts/CustomerContext";

const detailTabs = [
  { id: "booking", label: "Order Details", icon: "material-symbols:order-approve-outline" },
  { id: "transcripts", label: "Transcripts", icon: "gg:transcript" },
  { id: "analysis", label: "Analysis", icon: "carbon:flow-logs-vpc" },
  { id: "tickets", label: "Tickets", icon: "material-symbols:confirmation-number-outline" }
];

const CallDetails = ({ call, onClose }) => {
  const user = useAuthUser();
  const { getEffectiveCustomerId } = useCustomer();

  // Filter tabs based on client - hide Order Details for celeste/helios
  const clientId = getEffectiveCustomerId();
  const isHeliosCeleste = clientId === 'celeste' || clientId === 'helios';
  const availableTabs = isHeliosCeleste 
    ? detailTabs.filter(tab => tab.id !== 'booking')
    : detailTabs;

  const [activeTab, setActiveTab] = useState(availableTabs[0]?.id || 'transcripts');
  const currentTab = availableTabs.find((tab) => tab.id === activeTab);

  const [copiedId, setCopiedId] = useState(null);

  const [isSummary, setIsSummary] = useState(true);

  const [isLogs, setIsLogs] = useState(true);
  
  const [isInfoExpanded, setIsInfoExpanded] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  
  // Ensure activeTab is valid if client changes
  useEffect(() => {
    const isValidTab = availableTabs.some(tab => tab.id === activeTab);
    if (!isValidTab && availableTabs.length > 0) {
      setActiveTab(availableTabs[0].id);
    }
  }, [clientId, availableTabs, activeTab]);

  // Check if screen is mobile on mount and when window resizes
  useEffect(() => {
    const checkIfMobile = () => {
      const isMobileView = window.innerWidth < 768; // md breakpoint in Tailwind is 768px
      setIsMobile(isMobileView);
      
      // If switching from mobile to desktop, ensure content is visible
      if (!isMobileView && !isInfoExpanded) {
        setIsInfoExpanded(true);
      }
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, [isInfoExpanded]);

  if (!call) {
    return null; // Now it's safe to return early
  }

  const analysisTabChange = () => {
    setIsSummary((prev) => !prev);
  }

  const ticketsTabChange = () => {
    setIsLogs((prev) => !prev);
  }

  const renderContent = () => {
    switch(activeTab) {
      case "booking":
        return !isHeliosCeleste ? <BookingTab call={call} /> : null;
      case "transcripts":
        return <TranscriptsTab call={call} />;
      case "analysis":
        return <AnalyticsTab call={call} isSummary={isSummary} user={user} />;
      case "tickets":
        return <TicketsTab call={call} isLogs={isLogs} />;
      default:
        return null;
    }
  }

  const handleCopy = (callId) => {
      navigator.clipboard.writeText(callId);
      setCopiedId(callId);
      setTimeout(() => setCopiedId(null), 1000);
  }

  return (
    <div className="details-layout p-0 md:p-3 mr-2 mb-2 card-container rounded-md flex flex-col gap-4 text-sm h-full">
      <div className="flex-shrink-0 flex flex-row justify-between items-center gap-2 sm:gap-0">
        <div className="space-x-2 flex flex-row items-center gap-2 text-lg sm:text-xl font-semibold flex-shrink-0">
          <Icon icon="material-symbols:call-quality-rounded" className="text-xl sm:text-2xl text-primary"></Icon>
          Call Details
        </div>
        <div className="flex flex-row items-center gap-2 sm:gap-3 text-font-secondary w-fit min-w-0 overflow-hidden">
          <p className="text-xs sm:text-sm overflow-hidden text-ellipsis w-fit min-w-0 flex flex-row">
            Id: <span className="font-semibold text-ellipsis overflow-hidden">{call?.metadata.stream_sid}</span>
          </p>
          <button
            className="flex-shrink-0 text-xs sm:text-sm card-container bg-background text-accent p-1.5 sm:p-2 rounded"
            onClick={() => handleCopy(call?.metadata?.stream_sid)}
          >
            <Icon
              icon={
                copiedId === call.metadata.stream_sid
                ? 'mdi:check-bold'
                : 'solar:copy-linear'
              }
              className="text-sm sm:text-lg"
            />
          </button>
        </div>
      </div>
      <div className="flex-shrink-0 card-container bg-background p-0 rounded-md">
        {/* Mobile header with toggle button */}
        <div className="flex md:hidden justify-between items-center p-2 md:p-3 w-full cursor-pointer border-b border-gray-200 dark:border-gray-700" onClick={() => setIsInfoExpanded(!isInfoExpanded)}>
          <div className="flex items-center gap-2">
            <Icon icon="mdi:information-outline" className="text-secondary text-lg" />
            <span className="font-semibold">Call Information</span>
          </div>
          <Icon 
            icon={isInfoExpanded ? "mdi:chevron-up" : "mdi:chevron-down"} 
            className="text-secondary text-lg transition-transform duration-300" 
          />
        </div>
        
        {/* Content - Always visible on md+ screens, collapsible on mobile */}
        <div className={`
          ${isMobile ? (isInfoExpanded ? 'max-h-96' : 'max-h-0') : 'max-h-96'} 
          overflow-hidden transition-all duration-300 ease-in-out
          grid grid-cols-1 sm:grid-cols-3 gap-0
        `}>
          <div className="flex flex-col items-start gap-2 text-sm sm:text-base p-2 md:p-3 w-full">
            <div className="flex flex-row gap-1 items-center">
              <Icon icon="fa6-solid:mobile" className="text-secondary text-sm sm:text-base" />
              <span className="text-sm font-semibold">Phone Number</span>
            </div>
            <p className="text-font-secondary text-sm sm:text-base font-semibold">
              {call.metadata.caller_id}
            </p>
          </div>
          <div className="flex flex-col items-start gap-2 text-sm sm:text-base p-2 sm:p-3 w-full">
            <div className="flex flex-row gap-1 items-center">
              <Icon icon="fluent:hourglass-half-16-regular" className="text-secondary text-sm sm:text-lg" />
              <span className="text-sm font-semibold">Duration</span>
            </div>
            <p className="text-font-secondary text-sm sm:text-base font-semibold">
              {(() => {
                const durationSec = getCallDurationInSeconds(call);
                return durationSec > 0 ? formatDuration(durationSec) : 'No duration available';
              })()}
            </p>
          </div>
          <div className="flex flex-col items-start gap-2 text-sm sm:text-base p-2 sm:p-3 w-full">
            <div className="flex flex-row gap-1 items-center">
              <Icon icon="mingcute:time-duration-line" className="text-secondary text-sm sm:text-lg" />
              <span className="text-sm font-semibold">Started at</span>
            </div>
            <p className="text-font-secondary text-sm sm:text-base font-semibold">
              {call.metadata?.timestamp ? formatTimeStamp(call?.metadata?.timestamp) : 'No timestamp available'}
            </p>
          </div>
        </div>
      </div>
      <div className="flex-shrink-0 w-full flex flex-row items-center text-sm sm:text-base">
        {availableTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex flex-row justify-center items-center gap-1 p-2 min-w-0 whitespace-nowrap ${
              activeTab === tab.id ? "tickets-tab-active" : "tickets-tab-inactive"
            }`}
          >
            <Icon icon={tab.icon} className={`text-lg sm:text-xl ${
              activeTab === tab.id ? "text-primary" : ""
            }`} />
            <span className={`hidden sm:inline ${activeTab === tab.id ? 'font-semibold' : 'font-medium'}`}>
              {
                tab.id === 'analysis'
                ? (user.role === 'helios_admin' ? tab.label : 'Summary')
                : tab.label
              }
            </span>
          </button>
        ))}
      </div>
      <div className="flex-shrink-0 flex flex-col gap-4 items-start w-full flex-1 min-h-0">
          
          {currentTab.label === 'Analysis' ?
            <div className={`
              flex px-2 flex-row w-full items-start sm:items-center justify-between gap-2 sm:gap-0 flex-shrink-0
            `}>
              <div className={`flex flex-row justify-between font-semibold items-center gap-1 text-lg sm:text-xl text-font-primary`}>
                  <Icon icon={currentTab.icon} className="text-xl sm:text-2xl text-secondary" />
                  {user.role === 'helios_admin' ? currentTab.label : 'Summary'}
              </div>
              {user.role === 'helios_admin' ? (
                <div className="flex flex-row items-center w-fit gap-1" onClick={() => analysisTabChange()}>
                  <span className={`p-1.5 sm:p-2 rounded ${isSummary === true ? 'text-primary tag-secondary' : 'text-font-secondary'}`}>
                    <Icon icon="lineicons:text-paragraph" className="text-sm sm:text-lg" />
                  </span>
                  <span className={`p-1.5 sm:p-2 rounded ${isSummary === false ? 'text-accent tag-secondary' : 'text-font-secondary'}`}>
                    <Icon icon="ic:round-data-object" className="text-sm sm:text-lg" />
                  </span>
                </div>
              ) : (
                <div className={
                  `py-2 rounded-md flex items-center gap-2 text-sm sm:text-base
                  ${call.success_score >= 7 ? 'text-green-500' : call.success_score >= 4 ? 'text-yellow-500' : 'text-error'}
                `}>
                    <Icon icon="line-md:check-all" className="text-lg sm:text-xl" />
                    Score: {call.success_score || 'N/A'} ({call.score_status || 'N/A'})
                </div>
              )}
            </div>
            : currentTab.label === 'Tickets' ? (
            <div className={`
              flex px-2 flex-row w-full items-center gap-2 sm:gap-0 justify-between flex-shrink-0
            `}>
              <div className={`flex flex-row justify-center font-semibold items-center gap-1 text-lg sm:text-xl text-font-primary`}>
                  <Icon icon={currentTab.icon} className="text-xl sm:text-2xl text-secondary" />
                  {currentTab.label}
              </div>
              <div className="flex flex-row items-center w-fit gap-1" onClick={() => ticketsTabChange()}>
                <span className={`p-1.5 sm:p-2 rounded ${isLogs === true ? 'text-primary tag-secondary' : 'text-font-secondary'}`}>
                  <Icon icon="material-symbols:receipt-long-outline" className="text-sm sm:text-lg" />
                </span>
                <span className={`p-1.5 sm:p-2 rounded ${isLogs === false ? 'text-accent tag-secondary' : 'text-font-secondary'}`}>
                  <Icon icon="material-symbols:add-box-outline" className="text-sm sm:text-lg" />
                </span>
              </div>
            </div>
            ) :
            <div className={`flex flex-row justify-center font-semibold items-center px-2 gap-1 text-lg sm:text-xl text-font-primary flex-shrink-0`}>
                <Icon icon={currentTab.icon} className="text-xl sm:text-2xl text-secondary" />
                {currentTab.label}
            </div>
          }
          
          <div className="w-full flex-1 min-h-0 overflow-hidden">
            <div className="w-full h-full overflow-y-auto scrollable ">
              {renderContent()}
            </div>
          </div>
        </div>
    </div>
  );
  };
  
  export default CallDetails;
  